import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';

const OTPVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [email, setEmail] = useState('');
  const [notMemberError, setNotMemberError] = useState(false);

  useEffect(() => {
    // Get email from location state
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    } else {
      // Fallback to session storage for phone methods
      const storedContact = sessionStorage.getItem('memberContact');
      if (storedContact) {
        const contact = JSON.parse(storedContact);
        if (contact.method === 'email') {
          setEmail(contact.value);
        }
      } else {
        // No contact info, redirect to member sign in
        navigate('/member/signin');
        return;
      }
    }

    // Start resend timer
    const timer = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }

    // Clear errors when user starts typing
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: '' }));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to go to previous input
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const validateForm = () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setErrors({ general: 'Please enter all 6 digits' });
      return false;
    }
    if (!/^\d{6}$/.test(otpString)) {
      setErrors({ general: 'Please enter only digits' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const otpString = otp.join('');
      console.log('🔐 Verifying OTP:', otpString);
      
      // Verify email OTP with Supabase
      const { data, error } = await supabaseClient.auth.verifyOtp({
        email: email,
        token: otpString,
        type: 'email'
      });
      
      if (error) throw error;

      // Get the user session after verification
      const { data: { user } } = await supabaseClient.auth.getUser();

      if (!user) throw new Error('No user session after OTP verification');

      // Log OTP verified event to audit logs
      try {
        await supabaseClient
          .from('audit_logs')
          .insert({
            event_category: 'auth',
            event_type: 'otp_verified',
            actor_id: user.id,
            actor_role: 'member',
            new_value: { email },
            metadata: { contact_method: 'email' },
            risk_level: 'low',
            flagged: false,
            timestamp: new Date().toISOString(),
          });
        console.log('📝 OTP verified event logged to audit_logs');
      } catch (auditError) {
        console.error('❌ Failed to log OTP verified event:', auditError);
      }
      
      // Step 1 — Get authenticated user

      // Step 2 — Check if member record exists for this email
      // SECURITY DEFINER RPC — replaces the removed direct members-by-email read.
      // Returns rows of { id, auth_user_id }; take the first (or null).
      const { data: memberRows } = await supabaseClient
        .rpc('get_member_login_by_email', { p_email: email });
      const memberRecord = memberRows?.[0] ?? null;

      // Step 3 — If no member record exists
      if (!memberRecord) {
        await supabaseClient.auth.signOut();
        setNotMemberError(true);
        setErrors({ general: 'You are not a member of any group. Please ask your admin for an invite link.' });
        return;
      }

      // Step 4 — If member exists but auth_user_id is null, link it
      if (!memberRecord.auth_user_id) {
        await supabaseClient
          .from('members')
          .update({ auth_user_id: user.id })
          .eq('id', memberRecord.id);
      }

      // Step 5 — Navigate to member dashboard
      localStorage.setItem('userRole', 'member');
      localStorage.setItem('user', JSON.stringify({ 
        id: user.id,
        email: user.email
      }));
      
      navigate('/member/dashboard');

    } catch (err) {
      console.error('❌ Error verifying OTP:', err);
      setErrors({ general: 'Invalid code. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    
    setLoading(true);
    try {
      console.log('📧 Resending OTP to:', email);
      
      // Reset timer
      setResendTimer(30);
      
      // Clear OTP inputs
      setOtp(['', '', '', '', '', '']);
      
      // Focus first input
      const firstInput = document.getElementById('otp-0');
      if (firstInput) {
        firstInput.focus();
      }
      
    } catch (err) {
      console.error('❌ Error resending OTP:', err);
      setErrors({ general: 'Failed to resend code. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeMethod = () => {
    navigate('/member/signin');
  };

  const maskedContact = () => {
    if (!email) return '';
    // Email: mask the local part, keep the domain.
    if (email.includes('@')) {
      const [username, domain] = email.split('@');
      const maskedUsername = username.slice(0, 2) + '***' + username.slice(-1);
      return `${maskedUsername}@${domain}`;
    }
    // Phone-like contact: mask all but the last 4 digits (no bogus "@domain").
    const digits = email.replace(/\D/g, '');
    return `••• ••• ${digits.slice(-4)}`;
  };

  // Show different screen when notMemberError is true
  if (notMemberError) {
    return (
      <div className="text-center p-8">
        <p className="text-error-600 font-medium mb-4">
          You are not a member of any group.
        </p>
        <p className="text-neutral-600 mb-6">
          Please ask your admin for an invite link to join a group.
        </p>
        <button 
          onClick={() => { 
            supabaseClient.auth.signOut()
            navigate('/')
          }}
          className="px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg"
        >
          Back to Home
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50">
      {/* Header */}
      <div className="text-center py-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Verify Your Code</h1>
        <p className="text-neutral-600">
          Enter the 6-digit code we sent to {maskedContact()}
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Card>
            <div className="p-5 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* OTP Input */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-4">
                    Enter verification code
                  </label>
                  <div className="flex justify-center space-x-1.5 sm:space-x-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-9 h-9 sm:w-12 sm:h-12 text-center text-xl font-bold border-2 border-neutral-300 rounded-lg focus:ring-2 focus:ring-success-500 focus:border-transparent"
                        maxLength={1}
                        autoComplete="off"
                      />
                    ))}
                  </div>
                </div>

                {/* General Error */}
                {errors.general && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                    <p className="text-sm text-error-800">{errors.general}</p>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify →'}
                </Button>

                {/* Resend Code */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendTimer > 0 || loading}
                    className="text-sm text-success-600 hover:text-success-500 disabled:text-neutral-400 disabled:cursor-not-allowed"
                  >
                    {resendTimer > 0 
                      ? `Resend code in ${resendTimer}s` 
                      : 'Resend code'
                    }
                  </button>
                </div>

                {/* Change Method */}
                <div className="text-center pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleChangeMethod}
                    className="text-sm text-neutral-600 hover:text-neutral-500"
                  >
                    Change contact method
                  </button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-sm text-neutral-500">
          © 2026 EsuSol. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default OTPVerification;
